import React from 'react';
import { BalancesWidget } from './BalancesWidget';
import { TableRow } from './TableWidget';
import { useAuth } from '../../contexts/AuthContext';
import { fetchUserCoinbaseHoldings } from '../../api/coinbaseHoldings';
import { fetchTokenPrice } from '../../api/userPositions';
import { TOKENS } from '@sepori/shared/registry';

interface CoinbaseBalancesWidgetProps {
  className?: string;
}

export const CoinbaseBalancesWidget: React.FC<CoinbaseBalancesWidgetProps> = ({ className }) => {
  const { user } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<TableRow[]>([]);
  const [refreshing, setRefreshing] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState<Date | null>(null);

  const loadData = async (bypassCache = false) => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.id) {
        setRows([]);
        setLoading(false);
        return;
      }

      const authToken = localStorage.getItem('authToken') || '';
      const holdings = await fetchUserCoinbaseHoldings(user.id, authToken);

      // Map holdings with display_order for sorting
      const holdingsWithOrder = holdings.map(holding => {
        if (holding.token_symbol === 'USD') {
          return { ...holding, display_order: 0 };
        }
        const token = Object.values(TOKENS).find(t => t.symbol === holding.token_symbol);
        return { ...holding, display_order: token?.display_order ?? 999 };
      });

      // Sort by display_order (USD first, then registry tokens, then unknowns at end)
      const sortedHoldings = holdingsWithOrder.sort((a, b) => a.display_order - b.display_order);

      let totalValue = 0;
      const newRows: TableRow[] = [];

      for (const holding of sortedHoldings) {
        // Handle USD separately (fiat currency, fixed $1 price)
        if (holding.token_symbol === 'USD') {
          const value = holding.amount * 1; // USD is always $1
          totalValue += value;

          newRows.push({
            token: holding.token_symbol,
            amount: Number(holding.amount).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 6
            }),
            value: `$${value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}`
          });
          continue;
        }

        // Get token metadata from registry
        const token = Object.values(TOKENS).find(t => t.symbol === holding.token_symbol);
        if (!token) continue;

        // Fetch current price
        const priceData = await fetchTokenPrice(token.address, { bypassCache });
        const value = holding.amount * priceData.priceUsd;
        totalValue += value;

        newRows.push({
          token: holding.token_symbol,
          amount: Number(holding.amount).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 6
          }),
          value: `$${value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}`
        });
      }

      // Add total row
      newRows.push({
        token: '',
        amount: '',
        value: `$${totalValue.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`,
        valueClass: 'total-row-highlight'
      });

      setRows(newRows);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load coinbase balances:', err);
      setError(err instanceof Error ? err.message : 'Failed to load coinbase balances');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    setRefreshing(true);
    try {
      await loadData(true); // bypassCache=true for manual refresh
    } catch (err) {
      console.error('Failed to refresh coinbase balances:', err);
    } finally {
      setRefreshing(false);
    }
  };

  React.useEffect(() => {
    if (!user?.id) return;
    loadData();
  }, [user?.id]);

  return (
    <BalancesWidget
      title="Token Values"
      rows={rows}
      loading={loading}
      error={error}
      refreshing={refreshing}
      onRefresh={loading ? () => {} : refreshData}
      lastUpdated={lastUpdated ?? undefined}
      className={className}
    />
  );
};
